import {
  ProviderRegistrationGenderDisclosureInner,
  ProviderRegistrationGenderDisclosureInnerTypeEnum,
  ProviderRegistrationProfessionalCredentialsInner,
  ProviderRegistrationProfessionalCredentialsInnerTypeEnum,
} from "@asd/customer-portal-api";
import { emailValidator, formatDateTimeWithZone, parseTimestamp } from "@asd/customer-portal-ui";
import { Button, TextField } from "@mui/material";
import { Controller } from "react-hook-form";

import AddressPicker from "~/components/shared/AddressPicker";
import { DISTANCE_OPTIONS, GENDER_PREFERENCES, LANGUAGES, SERVICE_OPTIONS } from "~/utils/providerRegistrationHelpers";
import {
  BooleanEdit,
  ChoiceEdit,
  ConsiderationsEdit,
  CredentialsEdit,
  GenderDisclosureEdit,
  MultiChoiceEdit,
  PhoneTextEdit,
  TextEdit,
} from "./edits";

export interface RegistrationRow {
  label: string | React.ReactNode;
  key?: string;
  path?: string;
  isDirty?: boolean;
  value?: any;
  renderEdit?: () => React.ReactNode;
}

interface ConfigProps {
  control: any;
  dirtyFields: any;
  getRules: (requiredMessage?: string, additionalRules?: object) => any;
  updatedDate: string;
  isRequired: boolean;
  setShowSchedule: (show: boolean) => void;
}

const validateGenderDisclosure = (value: any, formValues: any) => {
  if (!formValues.publicProvider) {
    return true;
  }

  if (!value?.length) {
    return "Required";
  }

  const selfDescribed = value?.find(
    (v: ProviderRegistrationGenderDisclosureInner) => v.type === ProviderRegistrationGenderDisclosureInnerTypeEnum.self_described,
  );

  if (selfDescribed && !selfDescribed.selfDescription?.length) {
    return "Value is required if checked";
  }

  return true;
};

const validateConsiderations = (value: any, formValues: any) => {
  if (!formValues.publicProvider) {
    return true;
  }

  if (!value?.length) {
    return "Required";
  }

  return true;
};

const validateCredentials = (formValue: any, formValues: any) => {
  if (!formValues.publicProvider) {
    return true;
  }

  if (!formValue?.length) {
    return "Required";
  }

  const other = formValue.find(
    (v: ProviderRegistrationProfessionalCredentialsInner) => v.type === ProviderRegistrationProfessionalCredentialsInnerTypeEnum.other,
  );

  if (other && !other.otherText?.length) {
    return "Value is required if checked";
  }

  return true;
};

export const getRegistrationRows = ({
  control,
  dirtyFields,
  getRules,
  updatedDate,
  isRequired,
  setShowSchedule,
}: ConfigProps): RegistrationRow[] => {
  const isFieldDirty = (path: string) => !!dirtyFields[path];

  return [
    {
      label: "Last Updated",
      value: updatedDate ? formatDateTimeWithZone(parseTimestamp(updatedDate)) : "",
    },
    {
      label: "Do you wish to be listed in the provider directory to be contacted for new work?",
      path: "publicProvider",
      isDirty: isFieldDirty("publicProvider"),
      renderEdit: () => (
        <Controller
          control={control}
          name="publicProvider"
          render={({ field }) => <BooleanEdit onChange={field.onChange} value={field.value} />}
          rules={getRules()}
        />
      ),
    },
    {
      key: "providerName",
      label: (
        <>
          Provider name (e.g., Mary Smith, Example Company<sup>TM</sup>)
        </>
      ),
      path: "name",
      isDirty: isFieldDirty("name"),
      renderEdit: () => (
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => <TextEdit {...field} error={error?.message} />}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Phone Number",
      path: "phoneNumber",
      isDirty: isFieldDirty("phoneNumber"),
      renderEdit: () => <PhoneTextEdit control={control} isRequired={isRequired} name="phoneNumber" />,
    },
    {
      label: "Can receive texts",
      path: "phoneTextSupport",
      isDirty: isFieldDirty("phoneTextSupport"),
      renderEdit: () => (
        <Controller
          control={control}
          name="phoneTextSupport"
          render={({ field }) => <BooleanEdit onChange={field.onChange} value={field.value} />}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Email",
      path: "workEmailAddress",
      isDirty: isFieldDirty("workEmailAddress"),
      renderEdit: () => (
        <Controller
          control={control}
          name="workEmailAddress"
          render={({ field, fieldState: { error } }) => <TextEdit {...field} error={error?.message} />}
          rules={getRules(undefined, {
            ...emailValidator.pattern,
          })}
        />
      ),
    },
    {
      label: "Address",
      path: "address",
      isDirty: isFieldDirty("address"),
      renderEdit: () => (
        <Controller
          control={control}
          name="address"
          render={({ field, fieldState: { error } }) => (
            <AddressPicker
              error={!!error}
              helperText={error?.message}
              onValueChange={field.onChange}
              sx={{
                width: "100%",
                "&.MuiAutocomplete-root .MuiInputBase-root": {
                  py: 0,
                },
                "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                  transform: "translate(14px, 14px) scale(1)",
                },
              }}
              value={field.value}
              variant="standard"
              required
            />
          )}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Your gender identification",
      path: "genderDisclosure",
      isDirty: isFieldDirty("genderDisclosure"),
      renderEdit: () => (
        <Controller
          control={control}
          name="genderDisclosure"
          render={({ field, fieldState: { error } }) => (
            <GenderDisclosureEdit error={error?.message} onChange={field.onChange} value={field.value} />
          )}
          rules={{
            validate: validateGenderDisclosure,
          }}
        />
      ),
    },
    {
      label: "Language(s)",
      path: "languages",
      isDirty: isFieldDirty("languages"),
      renderEdit: () => (
        <Controller
          control={control}
          name="languages"
          render={({ field, fieldState: { error } }) => (
            <MultiChoiceEdit error={error?.message} onChange={field.onChange} options={LANGUAGES} value={field.value} />
          )}
          rules={getRules("At least one selection is required")}
        />
      ),
    },
    {
      label: "Service(s)",
      path: "services",
      isDirty: isFieldDirty("services"),
      renderEdit: () => (
        <Controller
          control={control}
          name="services"
          render={({ field, fieldState: { error } }) => (
            <MultiChoiceEdit error={error?.message} onChange={field.onChange} options={SERVICE_OPTIONS} value={field.value} />
          )}
          rules={getRules("At least one selection is required")}
        />
      ),
    },
    {
      label: "Commute independently",
      path: "commuteIndependently",
      isDirty: isFieldDirty("commuteIndependently"),
      renderEdit: () => (
        <Controller
          control={control}
          name="commuteIndependently"
          render={({ field }) => <BooleanEdit onChange={field.onChange} value={field.value} />}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Max miles willing to travel",
      path: "travelDistance",
      isDirty: isFieldDirty("travelDistance"),
      renderEdit: () => (
        <Controller
          control={control}
          name="travelDistance"
          render={({ field }) => (
            <ChoiceEdit
              onChange={(value) => {
                field.onChange(Number(value));
              }}
              options={DISTANCE_OPTIONS}
              value={String(field.value)}
            />
          )}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Max hours (per week)",
      path: "maxHoursWeek",
      isDirty: isFieldDirty("maxHoursWeek"),
      renderEdit: () => (
        <Controller
          control={control}
          defaultValue={null}
          name="maxHoursWeek"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              helperText={error ? error.message : null}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes(".")) {
                  e.target.value = value.split(".")[0];
                }
                field.onChange(e);
              }}
              onKeyDown={(e) => {
                if (e.key === "." || e.key === ",") e.preventDefault();
              }}
              slotProps={{
                htmlInput: { step: 1, min: 1, max: 168, pattern: "\\d*" },
              }}
              type="number"
              value={field.value ?? ""}
              variant="standard"
            />
          )}
          rules={getRules(undefined, {
            min: { value: 1, message: "Hours per week must be at least 1" },
            max: { value: 168, message: "Hours per week cannot exceed 168" },
            valueAsNumber: true,
          })}
        />
      ),
    },
    {
      label: "Availability",
      path: "generalAvailability",
      isDirty: isFieldDirty("generalAvailability"),
      renderEdit: () => (
        <Button onClick={() => setShowSchedule(true)} size="small">
          View / Edit
        </Button>
      ),
    },
    {
      label: "Work with minors",
      path: "workWithMinors",
      isDirty: isFieldDirty("workWithMinors"),
      renderEdit: () => (
        <Controller
          control={control}
          name="workWithMinors"
          render={({ field }) => <BooleanEdit onChange={field.onChange} value={field.value} />}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Employer gender preference",
      path: "genderPreference",
      isDirty: isFieldDirty("genderPreference"),
      renderEdit: () => (
        <Controller
          control={control}
          name="genderPreference"
          render={({ field }) => <ChoiceEdit onChange={field.onChange} options={GENDER_PREFERENCES} value={field.value} />}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Considerations",
      path: "considerations",
      isDirty: isFieldDirty("considerations"),
      renderEdit: () => (
        <Controller
          control={control}
          name="considerations"
          render={({ field, fieldState: { error } }) => (
            <ConsiderationsEdit error={error?.message} onChange={field.onChange} value={field.value} />
          )}
          rules={{
            validate: validateConsiderations,
          }}
        />
      ),
    },
    {
      label: "Previous experience",
      path: "caregiverExperience",
      isDirty: isFieldDirty("caregiverExperience"),
      renderEdit: () => (
        <Controller
          control={control}
          name="caregiverExperience"
          render={({ field }) => <BooleanEdit onChange={field.onChange} value={field.value} />}
          rules={getRules()}
        />
      ),
    },
    {
      label: "Credentials",
      path: "professionalCredentials",
      isDirty: isFieldDirty("professionalCredentials"),
      renderEdit: () => (
        <Controller
          control={control}
          name="professionalCredentials"
          render={({ field, fieldState: { error } }) => (
            <CredentialsEdit error={error?.message} onChange={field.onChange} value={field.value} />
          )}
          rules={{
            validate: validateCredentials,
          }}
        />
      ),
    },
  ];
};
